import { gql, useMutation } from '@apollo/client';
import {
  createTemplate as CREATE_TEMPLATE,
  updateTemplate as UPDATE_TEMPLATE,
  TemplateStatus,
} from '@admiin-com/ds-graphql';
import { Template } from '@admiin-com/ds-graphql';
import { useCurrentEntityId } from '../useSelectedEntity/useSelectedEntity';

export type TemplateFormData = {
  title: string;
  templateJson?: string;
  templateHtml?: string;
  includeLetterhead?: boolean;
};

export const useTemplateMutations = () => {
  const entityId = useCurrentEntityId();

  const [createTemplateMutation, { loading: creating }] = useMutation(
    gql(CREATE_TEMPLATE),
    {
      update(cache, { data }) {
        cache.modify({
          fields: {
            templatesByEntity(existingTemplates = { items: [] }) {
              const newTemplateRef = cache.writeFragment({
                data: data.createTemplate,
                fragment: gql`
                  fragment NewTemplate on Template {
                    id
                    entityId
                    title
                    templateJson
                    status
                    includeLetterhead
                    createdAt
                    updatedAt
                  }
                `,
              });
              return {
                ...existingTemplates,
                items: existingTemplates.items.concat(newTemplateRef),
              };
            },
          },
        });
      },
    }
  );

  const [updateTemplateMutation, { loading: updating }] = useMutation(
    gql(UPDATE_TEMPLATE)
  );

  const createTemplate = async (data: TemplateFormData): Promise<Template> => {
    const createdTemplateData = await createTemplateMutation({
      variables: {
        input: {
          entityId,
          title: data.title,
          templateJson: data.templateJson,
          status: TemplateStatus.ACTIVE,
          templateHtml: data.templateHtml,
          includeLetterhead: data.includeLetterhead,
        },
      },
    });

    return createdTemplateData.data.createTemplate;
  };

  const updateTemplate = async (
    id: string,
    data: TemplateFormData
  ): Promise<Template> => {
    const updatedTemplateData = await updateTemplateMutation({
      variables: {
        input: {
          id,
          title: data.title,
          templateJson: data.templateJson,
          templateHtml: data.templateHtml,
          includeLetterhead: data.includeLetterhead,
        },
      },
    });

    return updatedTemplateData.data.updateTemplate;
  };

  return { createTemplate, updateTemplate, creating, updating };
};
